import { useState, useRef, useCallback, useEffect } from 'react';
import { Mp3Encoder } from '@breezystack/lamejs';

export type AudioPermissionStatus = 'prompt' | 'granted' | 'denied' | 'not_found' | 'unsupported';

export interface UseAudioRecorderOptions {
  maxDurationSeconds?: number; // Defaults to 300 (5 minutes)
  minDurationSeconds?: number; // Defaults to 2 seconds debounce
  onMaxDurationReached?: () => void;
}

export interface UseAudioRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  duration: number; // in seconds
  formattedDuration: string; // "MM:SS"
  audioBlob: Blob | null;
  audioUrl: string | null;
  volumeLevel: number; // 0 - 100
  canStop: boolean; // true when duration >= minDurationSeconds
  permissionStatus: AudioPermissionStatus;
  errorMessage: string | null;
  startRecording: () => Promise<boolean>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
  cancelRecording: () => void;
  clearAudio: () => void;
  clearError: () => void;
}

export function formatAudioDuration(seconds: number): string {
  if (typeof seconds !== 'number' || isNaN(seconds) || !isFinite(seconds) || seconds <= 0) {
    return '00:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Encodes an array of Float32Array PCM chunks into an MP3 Blob using lamejs.
 */
function encodePCMToMp3(chunks: Float32Array[], sampleRate: number): Blob {
  let totalLength = 0;
  for (let i = 0; i < chunks.length; i++) {
    totalLength += chunks[i].length;
  }

  const fullPcm = new Float32Array(totalLength);
  let offset = 0;
  for (let i = 0; i < chunks.length; i++) {
    fullPcm.set(chunks[i], offset);
    offset += chunks[i].length;
  }

  // Convert Float32Array (-1.0 to 1.0) to Int16Array (-32768 to 32767)
  const int16Samples = new Int16Array(fullPcm.length);
  for (let i = 0; i < fullPcm.length; i++) {
    const s = Math.max(-1, Math.min(1, fullPcm[i]));
    int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }

  // 1 channel (mono), 128 kbps
  const encoder = new Mp3Encoder(1, sampleRate, 128);
  const mp3Data: Uint8Array[] = [];
  const blockSize = 1152;

  for (let i = 0; i < int16Samples.length; i += blockSize) {
    const chunk = int16Samples.subarray(i, i + blockSize);
    const mp3buf = encoder.encodeBuffer(chunk);
    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }
  }

  const endBuf = encoder.flush();
  if (endBuf.length > 0) {
    mp3Data.push(endBuf);
  }

  return new Blob(mp3Data as BlobPart[], { type: 'audio/mp3' });
}

export const useAudioRecorder = (options: UseAudioRecorderOptions = {}): UseAudioRecorderReturn => {
  const {
    maxDurationSeconds = 300, // 5 minutes
    minDurationSeconds = 2, // 2 seconds minimum before stopping
    onMaxDurationReached,
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [permissionStatus, setPermissionStatus] = useState<AudioPermissionStatus>('prompt');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const maxDurationSecondsRef = useRef(maxDurationSeconds);
  const minDurationSecondsRef = useRef(minDurationSeconds);
  const onMaxDurationReachedRef = useRef(onMaxDurationReached);

  useEffect(() => {
    maxDurationSecondsRef.current = maxDurationSeconds;
  }, [maxDurationSeconds]);

  useEffect(() => {
    minDurationSecondsRef.current = minDurationSeconds;
  }, [minDurationSeconds]);

  useEffect(() => {
    onMaxDurationReachedRef.current = onMaxDurationReached;
  }, [onMaxDurationReached]);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<any | null>(null);
  const durationRef = useRef(0);
  const pcmChunksRef = useRef<Float32Array[]>([]);
  const totalSamplesRef = useRef(0);
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);
  const errorTimerRef = useRef<any | null>(null);

  const clearError = useCallback(() => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    setErrorMessage(null);
  }, []);

  // Keep duration ref in sync for interval check
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  // Keep paused ref in sync
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Keep recording ref in sync
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Cleanup helper
  const cleanupStreamAndAudioContext = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => null);
      audioContextRef.current = null;
    }
    setVolumeLevel(0);
  }, []);

  // Stop recording internal and encode to MP3
  const stopRecordingInternal = useCallback(() => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;

    const currentSampleRate = audioContextRef.current?.sampleRate || 44100;
    const collectedChunks = [...pcmChunksRef.current];

    cleanupStreamAndAudioContext();
    setIsRecording(false);
    setIsPaused(false);
    isPausedRef.current = false;

    if (collectedChunks.length > 0) {
      try {
        const mp3Blob = encodePCMToMp3(collectedChunks, currentSampleRate);
        setAudioBlob(mp3Blob);
        const url = URL.createObjectURL(mp3Blob);
        setAudioUrl(url);
      } catch (err) {
        console.error('Error encoding MP3 audio:', err);
      }
    }
  }, [cleanupStreamAndAudioContext]);

  // Trigger limit reached auto-stop
  const handleReachMaxLimit = useCallback(() => {
    if (!isRecordingRef.current) return;
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setDuration(maxDurationSecondsRef.current);
    stopRecordingInternal();
    onMaxDurationReachedRef.current?.();
  }, [stopRecordingInternal]);

  // Start audio level visualizer loop
  const startVolumeAnalyser = useCallback((analyser: AnalyserNode) => {
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateVolume = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const avg = sum / dataArray.length;
      const normalized = Math.min(100, Math.round((avg / 128) * 100));
      setVolumeLevel(normalized);

      animFrameRef.current = requestAnimationFrame(updateVolume);
    };

    updateVolume();
  }, []);

  // Start recording
  const startRecording = useCallback(async (): Promise<boolean> => {
    clearError();

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      setPermissionStatus('unsupported');
      setErrorMessage(
        'Audio recording is not supported in this browser. Please try Chrome, Firefox, or Safari.',
      );
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      mediaStreamRef.current = stream;
      setPermissionStatus('granted');

      // Initialize AudioContext
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioContextRef.current = audioCtx;

      const sourceNode = audioCtx.createMediaStreamSource(stream);
      sourceNodeRef.current = sourceNode;

      // Analyser for waveform / volume level
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.8;
      sourceNode.connect(analyser);
      analyserRef.current = analyser;

      // ScriptProcessor to capture raw PCM chunks
      const bufferSize = 4096;
      const processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
      processorNodeRef.current = processor;
      pcmChunksRef.current = [];
      totalSamplesRef.current = 0;

      processor.onaudioprocess = (e) => {
        if (isPausedRef.current || !isRecordingRef.current) return;
        const channelData = e.inputBuffer.getChannelData(0);
        const sampleRate = audioContextRef.current?.sampleRate || 44100;
        const maxSamples = sampleRate * maxDurationSecondsRef.current;

        if (totalSamplesRef.current + channelData.length >= maxSamples) {
          const remaining = Math.max(0, maxSamples - totalSamplesRef.current);
          if (remaining > 0) {
            pcmChunksRef.current.push(new Float32Array(channelData.subarray(0, remaining)));
            totalSamplesRef.current += remaining;
          }
          handleReachMaxLimit();
          return;
        }

        pcmChunksRef.current.push(new Float32Array(channelData));
        totalSamplesRef.current += channelData.length;
      };

      sourceNode.connect(processor);
      // Connect to a silent/zero gain node or destination to keep processor active in all browsers
      const silentGain = audioCtx.createGain();
      silentGain.gain.value = 0;
      processor.connect(silentGain);
      silentGain.connect(audioCtx.destination);

      isRecordingRef.current = true;
      setIsRecording(true);
      setIsPaused(false);
      isPausedRef.current = false;
      setDuration(0);

      // Start volume analyser
      startVolumeAnalyser(analyser);

      // Start duration timer
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = setInterval(() => {
        setDuration((prev) => {
          const next = prev + 1;
          if (next >= maxDurationSecondsRef.current) {
            handleReachMaxLimit();
            return maxDurationSecondsRef.current;
          }
          return next;
        });
      }, 1000);

      return true;
    } catch (err: unknown) {
      const error = err as { name?: string; message?: string };
      let friendlyMsg = 'Failed to start recording. Please check microphone permissions.';

      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError' ||
        (error.message && error.message.toLowerCase().includes('permission'))
      ) {
        setPermissionStatus('denied');
        friendlyMsg =
          'Microphone access is blocked in your browser settings. Please allow microphone access to record voice notes.';
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError' ||
        (error.message && error.message.toLowerCase().includes('not found'))
      ) {
        setPermissionStatus('not_found');
        friendlyMsg =
          'No microphone was detected on this device. Please connect a microphone and try again.';
      } else if (error.name === 'NotReadableError') {
        setPermissionStatus('denied');
        friendlyMsg = 'Your microphone is currently in use by another application.';
      } else {
        setPermissionStatus('denied');
        friendlyMsg = error.message || friendlyMsg;
      }

      setErrorMessage(friendlyMsg);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = setTimeout(() => {
        setErrorMessage(null);
      }, 6000);
      return false;
    }
  }, [clearError, handleReachMaxLimit, startVolumeAnalyser]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (!isRecordingRef.current || isPausedRef.current) return;
    setIsPaused(true);
    isPausedRef.current = true;
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (!isRecordingRef.current || !isPausedRef.current) return;
    setIsPaused(false);
    isPausedRef.current = false;

    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      setDuration((prev) => {
        const next = prev + 1;
        if (next >= maxDurationSecondsRef.current) {
          handleReachMaxLimit();
          return maxDurationSecondsRef.current;
        }
        return next;
      });
    }, 1000);
  }, [handleReachMaxLimit]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) return;
    if (durationRef.current < minDurationSecondsRef.current) {
      return; // Debounce guard: cannot stop before minimum duration
    }
    stopRecordingInternal();
  }, [stopRecordingInternal]);

  // Cancel / discard recording
  const cancelRecording = useCallback(() => {
    isRecordingRef.current = false;
    cleanupStreamAndAudioContext();
    pcmChunksRef.current = [];
    totalSamplesRef.current = 0;
    setIsRecording(false);
    setIsPaused(false);
    isPausedRef.current = false;
    setDuration(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
  }, [cleanupStreamAndAudioContext, audioUrl]);

  // Clear recorded audio
  const clearAudio = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    pcmChunksRef.current = [];
    totalSamplesRef.current = 0;
  }, [audioUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStreamAndAudioContext();
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [cleanupStreamAndAudioContext, audioUrl]);

  return {
    isRecording,
    isPaused,
    duration,
    formattedDuration: formatAudioDuration(Math.min(duration, maxDurationSeconds)),
    audioBlob,
    audioUrl,
    volumeLevel,
    canStop: duration >= minDurationSeconds,
    permissionStatus,
    errorMessage,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    clearAudio,
    clearError,
  };
};
